<template>
  <header class="bg-white shadow-md sticky top-0 z-[1000]">
    <nav class="container mx-auto px-4 py-4">
      <!-- Desktop Header -->
      <div class="hidden md:flex items-center justify-between">
        <router-link :to="localePath('/')" class="text-2xl font-bold text-gray-800 hover:text-blue-600 transition">
          <SiteBrandName />
        </router-link>
        <div class="flex items-center gap-6">
          <ul class="flex flex-wrap gap-x-6 gap-y-2 items-center list-none m-0 p-0">
            <template v-if="isTenant">
              <li class="flex items-center">
                <router-link
                  :to="localePath('/property')"
                  class="inline-flex items-center text-gray-700 hover:text-blue-600 transition font-medium pb-1 border-b-2 border-transparent"
                  active-class="text-blue-600 border-blue-600"
                >
                  {{ $t('nav.property') }}
                </router-link>
              </li>
              <li class="flex items-center">
                <router-link
                  :to="localePath('/location')"
                  class="inline-flex items-center text-gray-700 hover:text-blue-600 transition font-medium pb-1 border-b-2 border-transparent"
                  active-class="text-blue-600 border-blue-600"
                >
                  {{ $t('nav.location') }}
                </router-link>
              </li>
              <li class="flex items-center">
                <router-link
                  :to="localePath('/availability')"
                  class="inline-flex items-center text-gray-700 hover:text-blue-600 transition font-medium pb-1 border-b-2 border-transparent"
                  active-class="text-blue-600 border-blue-600"
                >
                  {{ $t('nav.availability') }}
                </router-link>
              </li>
            </template>
            <template v-else>
              <li class="flex items-center">
                <router-link
                  :to="localePath('/property')"
                  class="inline-flex items-center text-gray-700 hover:text-blue-600 transition font-medium pb-1 border-b-2 border-transparent"
                  active-class="text-blue-600 border-blue-600"
                >
                  {{ $t('nav.property') }}
                </router-link>
              </li>
              <li class="flex items-center">
                <router-link
                  :to="localePath('/location')"
                  class="inline-flex items-center text-gray-700 hover:text-blue-600 transition font-medium pb-1 border-b-2 border-transparent"
                  active-class="text-blue-600 border-blue-600"
                >
                  {{ $t('nav.location') }}
                </router-link>
              </li>
              <li class="flex items-center">
                <router-link
                  :to="localePath('/availability')"
                  class="inline-flex items-center text-gray-700 hover:text-blue-600 transition font-medium pb-1 border-b-2 border-transparent"
                  active-class="text-blue-600 border-blue-600"
                >
                  {{ $t('nav.availability') }}
                </router-link>
              </li>
            </template>
          </ul>
          
          <div class="flex items-center gap-3">
            <router-link
              v-if="isTenant"
              to="/dashboard"
              class="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
            >
              <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
              {{ $t('nav.dashboard') }}
            </router-link>
            <router-link
              v-if="isAdmin"
              to="/admin"
              class="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
            >
              <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
              {{ $t('nav.adminDashboard') }}
            </router-link>

            <LanguageSwitcher />
            
            <!-- Auth buttons -->
            <div v-if="isAuthenticated" class="flex items-center gap-3">
              <router-link
                v-if="isAdmin && userNicknameInitials"
                to="/admin/profile"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-700 transition hover:bg-gray-200"
                :aria-label="$t('nav.profileSettings')"
                :title="userNicknameDisplay"
              >
                {{ userNicknameInitials }}
              </router-link>
              <router-link
                v-else-if="isTenant && userNicknameInitials"
                to="/dashboard/profile"
                class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-700 transition hover:bg-gray-200"
                :aria-label="$t('nav.profileSettings')"
                :title="userNicknameDisplay"
              >
                {{ userNicknameInitials }}
              </router-link>

              <!-- Logout button -->
              <button
                @click="handleLogout"
                class="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {{ $t('auth.logout') }}
              </button>
            </div>
            
            <!-- Login button -->
            <router-link
              v-else
              to="/login"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition flex items-center gap-2"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              {{ $t('auth.login') }}
            </router-link>
          </div>
        </div>
      </div>
      
      <!-- Mobile Header -->
      <div class="md:hidden">
        <div class="flex items-center justify-between mb-3">
          <router-link :to="localePath('/')" class="text-xl font-bold text-gray-800 hover:text-blue-600 transition">
            <SiteBrandName />
          </router-link>
          <div class="flex items-center gap-3">
            <LanguageSwitcher />
            <button
              @click="isMobileMenuOpen = !isMobileMenuOpen"
              class="p-2 text-gray-700 hover:text-blue-600 transition"
              :aria-label="$t('nav.toggleMenuAria')"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  v-if="!isMobileMenuOpen"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
                <path
                  v-else
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
        
        <!-- Mobile Menu -->
        <Transition name="slide-down">
          <div v-if="isMobileMenuOpen" class="pt-4 border-t border-gray-200 pb-2">
            <ul class="space-y-2">
              <template v-if="isTenant">
                <li>
                  <button
                    type="button"
                    class="w-full flex items-center justify-between px-2 py-2 text-gray-700 hover:bg-gray-50 rounded transition font-medium text-left"
                    :class="propertyMobileOpen || isPropertySectionActive ? 'text-blue-600 bg-blue-50' : ''"
                    @click="propertyMobileOpen = !propertyMobileOpen"
                  >
                    {{ $t('nav.property') }}
                    <svg
                      class="w-4 h-4 transition-transform shrink-0"
                      :class="{ 'rotate-180': propertyMobileOpen }"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <ul v-show="propertyMobileOpen" class="pl-3 mt-1 space-y-1 border-l border-gray-200 ml-2">
                    <li>
                      <router-link
                        :to="localePath('/property')"
                        class="block px-2 py-1.5 text-sm text-gray-600 hover:text-blue-600 rounded"
                        @click="closeMobileNav"
                      >
                        {{ $t('nav.property') }}
                      </router-link>
                    </li>
                    <li>
                      <router-link
                        :to="localePath('/location')"
                        class="block px-2 py-1.5 text-sm text-gray-600 hover:text-blue-600 rounded"
                        @click="closeMobileNav"
                      >
                        {{ $t('nav.location') }}
                      </router-link>
                    </li>
                    <li>
                      <router-link
                        :to="localePath('/availability')"
                        class="block px-2 py-1.5 text-sm text-gray-600 hover:text-blue-600 rounded"
                        @click="closeMobileNav"
                      >
                        {{ $t('nav.availability') }}
                      </router-link>
                    </li>
                  </ul>
                </li>
              </template>
              <template v-else>
                <li>
                  <router-link
                    :to="localePath('/property')"
                    class="block px-2 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded transition font-medium"
                    active-class="text-blue-600 bg-blue-50"
                    @click="closeMobileNav"
                  >
                    {{ $t('nav.property') }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="localePath('/location')"
                    class="block px-2 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded transition font-medium"
                    active-class="text-blue-600 bg-blue-50"
                    @click="closeMobileNav"
                  >
                    {{ $t('nav.location') }}
                  </router-link>
                </li>
                <li>
                  <router-link
                    :to="localePath('/availability')"
                    class="block px-2 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded transition font-medium"
                    active-class="text-blue-600 bg-blue-50"
                    @click="closeMobileNav"
                  >
                    {{ $t('nav.availability') }}
                  </router-link>
                </li>
              </template>
              <li>
                <router-link
                  :to="localePath('/feedback')"
                  class="block px-2 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded transition font-medium"
                  active-class="text-blue-600 bg-blue-50"
                  @click="closeMobileNav"
                >
                  {{ $t('feedback.title') }}
                </router-link>
              </li>
              <li v-if="isTenant">
                <router-link
                  to="/dashboard"
                  class="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                  @click="closeMobileNav"
                >
                  <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  {{ $t('nav.dashboard') }}
                </router-link>
              </li>

              <!-- Admin link for mobile -->
              <li v-if="isAdmin">
                <router-link
                  to="/admin"
                  @click="closeMobileNav"
                  class="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
                >
                  <svg class="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                  {{ $t('nav.adminDashboard') }}
                </router-link>
              </li>
              
              <!-- Auth section for mobile -->
              <li class="border-t border-gray-200 pt-2 mt-2">
                <div v-if="isAuthenticated" class="space-y-2">
                  <router-link
                    v-if="isAdmin"
                    to="/admin/profile"
                    @click="closeMobileNav"
                    class="block px-2 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded transition font-medium"
                  >
                    {{ $t('nav.profileSettings') }}
                  </router-link>
                  <router-link
                    v-else-if="isTenant"
                    to="/dashboard/profile"
                    @click="closeMobileNav"
                    class="block px-2 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded transition font-medium"
                  >
                    {{ $t('nav.profileSettings') }}
                  </router-link>
                  <button
                    @click="handleLogout"
                    class="w-full text-left px-2 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition font-medium"
                  >
                    {{ $t('auth.logout') }}
                  </button>
                </div>
                <router-link
                  v-else
                  to="/login"
                  @click="closeMobileNav"
                  class="block px-2 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition font-medium"
                >
                  {{ $t('auth.login') }}
                </router-link>
              </li>
            </ul>
          </div>
        </Transition>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import LanguageSwitcher from './LanguageSwitcher.vue';
import SiteBrandName from '../common/SiteBrandName.vue';
import { authStore } from '../../stores/auth';
import { CONFIG } from '../../config';
import { getLocaleFromCookie } from '../../utils/localeCookie.js';

const route = useRoute();
const router = useRouter();
const { locale } = useI18n();
const isMobileMenuOpen = ref(false);
const propertyMobileOpen = ref(false);

const PROPERTY_ROUTE_NAMES = new Set(['home', 'property', 'location', 'availability']);

const isAuthenticated = computed(() => authStore.isAuthenticated);
const isAdmin = computed(() => authStore.isAdmin);
const isTenant = computed(() => authStore.isTenant);

const userNicknameDisplay = computed(() => {
  const nick = authStore.state.user?.nickname;
  return nick != null && String(nick).trim() !== '' ? String(nick).trim() : '';
});

/** First letter of each word in nickname, max 2 letters. */
const userNicknameInitials = computed(() => {
  const nick = userNicknameDisplay.value;
  if (!nick) return '';
  return nick
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
});

const isPropertySectionActive = computed(() => {
  const n = route.name;
  return typeof n === 'string' && PROPERTY_ROUTE_NAMES.has(n);
});

watch(
  () => route.fullPath,
  () => {
    closeMobileNav();
  }
);

function closeMobileNav() {
  isMobileMenuOpen.value = false;
  propertyMobileOpen.value = false;
}

// Build localized path for SSG routes
// Always reads from cookie first (source of truth) to avoid stale i18n locale
function localePath(path) {
  let lang;
  
  // 1. Check route params first (for SSG routes)
  if (route.params.lang && CONFIG.SUPPORTED_LOCALES.includes(route.params.lang)) {
    lang = route.params.lang;
  } 
  // 2. Check cookie (source of truth)
  else if (typeof document !== 'undefined') {
    const cookieLocale = getLocaleFromCookie();
    if (cookieLocale) {
      lang = cookieLocale;
    }
  }
  
  // 3. Fallback to i18n locale or default
  if (!lang) {
    lang = locale.value || CONFIG.DEFAULT_LOCALE;
  }
  
  if (path === '/') {
    return `/${lang}`;
  }
  return `/${lang}${path}`;
}

async function handleLogout() {
  await authStore.logout();
  closeMobileNav();
  
  // Get locale from cookie (source of truth) and sync i18n before redirect
  // TODO: check with getLocaleFromCookie and potentially merge
  const cookieLocale = CONFIG.getLocaleFromClient();
  if (cookieLocale && cookieLocale !== locale.value) {
    locale.value = cookieLocale;
  }
  
  // Use cookie directly for redirect path (bypasses computed which might be stale)
  const redirectPath = localePath('/');
  router.push(redirectPath);
}
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter-from {
  opacity: 0;
  max-height: 0;
}

.slide-down-enter-to {
  opacity: 1;
  max-height: 500px;
}

.slide-down-leave-from {
  opacity: 1;
  max-height: 500px;
}

.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}
</style>